import type { FC } from "react";
import { useTranslation } from "react-i18next";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@ui/Form";
import { QUERY_KEYS } from "@/constants/queryKeys";
import type { Operator, Tariff } from "@/types/Operator";
import { useMultiStepForm } from "@/hooks";
import { cn } from "@/utils/tailwind";
import { OperatorApi } from "@/api/OperatorApi";
import { PaginatedSelect } from "@/components/Selects";
import type { StepProps } from "./StepProps";

export const Step2: FC<StepProps> = ({ additionalItems }) => {
  const { t } = useTranslation();
  const { form } = useMultiStepForm();

  const operatorId = form.watch("step2.operator")?.id ?? 0;

  return (
    <div className="space-y-6 sm:space-y-[30px] lg:space-y-10">
      <FormField
        control={form.control}
        name="step2.operator"
        render={({ field }) => (
          <FormItem>
            <FormLabel data-test="operator-label">{t("step2.q1")}</FormLabel>
            <FormControl>
              <div className="space-y-0.5 sm:px-7" data-test="operator-field">
                <FormDescription data-test="operator-description">
                  {t("step2.q1_operator")}
                </FormDescription>
                <PaginatedSelect<Operator>
                  queryKey={[QUERY_KEYS.OPERATORS]}
                  fetchFn={OperatorApi.getAll}
                  getOptionLabel={op => op.name}
                  getOptionValue={op => op.id.toString()}
                  isError={Boolean(form.formState.errors.step2?.operator)}
                  value={field.value?.id.toString() ?? ""}
                  onSelectItem={operator => {
                    field.onChange(operator);
                    form.setValue("step2.tariff", { id: 0, name: "", operator: operator.id });
                  }}
                  dataTest="operator-select"
                />
              </div>
            </FormControl>
            <FormMessage className="-mt-3 sm:px-7" data-test="operator-error-message" />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="step2.tariff"
        render={({ field }) => (
          <FormItem>
            <FormLabel className={cn(!operatorId && "text-[#868686]")} data-test="tariff-label">
              {t("step2.q2")}
            </FormLabel>
            <FormControl>
              <div className="space-y-0.5 sm:px-7" data-test="tariff-field">
                <FormDescription
                  className={cn(!operatorId && "text-input")}
                  data-test="tariff-description"
                >
                  {t("step2.q2_tariff")}
                </FormDescription>
                <PaginatedSelect<Tariff>
                  queryKey={[QUERY_KEYS.TARIFFS, { operatorId }]}
                  fetchFn={(limit, offset) => OperatorApi.getAllTariffs(limit, offset, operatorId)}
                  getOptionLabel={tariff => tariff.name}
                  getOptionValue={tariff => tariff.id.toString()}
                  isError={Boolean(form.formState.errors.step2?.tariff)}
                  value={field.value?.id.toString() ?? ""}
                  onSelectItem={field.onChange}
                  disabled={!operatorId}
                  dataTest="tariff-select"
                />
              </div>
            </FormControl>
            <FormMessage className="-mt-3 sm:px-7" data-test="tariff-error-message" />
          </FormItem>
        )}
      />
      {additionalItems}
    </div>
  );
};
